import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import Routes from '../../constants/routes';
import SignInForm from './SignInForm';
import { actions as accountActions } from '../../actions/accountActions';
import { actions as userAccessRequestActions } from '../../actions/userAccessRequestActions';
import { actions as mfaActions } from '../../actions/mfaActions';
import Notification from '../Common/Notification';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import LoginStyleWrapper from '../Common/LoginStyleWrapper/LoginStyleWrapper';
import URLSearchParams from '@ungap/url-search-params'; // URLSearchParams polyfill for IE11
import * as types from '../../constants/actionTypes';
import { CUSTOM_LOGIN_SUBSCRIPTIONS } from '../../constants/constants';
import TfaSignInForm from './TfaSignInForm';

export class LoginPage extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            isLoading: false,
            isFormValid: true,
            showPasswordField: false,
            username: '',
            password: '',
            subscriptionId: this.setSubscriptionFromSubdomain(),
            redirectURL: null,
            error: null,
            token: null,
            redirectToUserPage: null,

            show2fa: false,
            twoFactorAuthCode: '',
            is2faFormValid: true,
        };
    }

    componentDidMount() {
        this.props.accountActions.logout();
        this.parseQueryStrings();
    }

    parseQueryStrings = () => {
        const urlParams = new URLSearchParams(this.props.location.search);
        let redirectURL, error, token, redirectToUserPage, username = null;

        if (urlParams.has('redirectURL')) {
            redirectURL = urlParams.get('redirectURL');
        }
    
        if (urlParams.has('error')) {
            error = urlParams.get('error');
        }
    
        if (urlParams.has('token')) {
            token = urlParams.get('token');
        }
    
        if (urlParams.has('up')) {
            redirectToUserPage = urlParams.get('up') === '1' ? true : false;
        }

        if (urlParams.has('username')) {
            username = urlParams.get('username');
        }
        
        this.setState({
            ...this.state,
            redirectURL: redirectURL,
            error: error,
            token: token,
            redirectToUserPage: redirectToUserPage,
            username: username
        }, () => this.actionQueryStrings());
    }

    actionQueryStrings = () => {
        if (this.state.redirectURL) {
            this.props.accountActions.setRedirectURL(this.state.redirectURL);
        }

        //if a token exists in the url params then we are continuing an sso login process and do not want to
        //clear local storage and app state
        if (this.state.token) {

            //the mobile app injects ReactNativeWebView into this page so we can reliably use it to distinguish if Mobile
            //the token is then passed to the mobile app to perform a post SSO login workflow
            if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(this.state.token);
            } else {
                this.setState({ isLoading: true });

                try {
                    this.setToken();
                    this.postSsologin();
                } catch (error) {
                    Notification("error", "An Error Occurred", error);
                    this.setState({ isLoading: false });
                }
            }
        }

        if (this.state.username) {
            this.findUser();
        }

        if (this.state.error) {
            if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(this.state.error);
            }
            else {
                Notification("error", "SSO Error", this.state.error);
            }
        }
    }

    setSubscriptionFromSubdomain = () => {
        const subdomain = window.location.host.split(".")[0]; //this will grab the first segment of the url before the dot
        const subdomainLower = subdomain ? subdomain.toLowerCase() : '';
        let subscriptionId = null;

        switch (subdomainLower) {
            case CUSTOM_LOGIN_SUBSCRIPTIONS.TheDrew.name + "test":
            case CUSTOM_LOGIN_SUBSCRIPTIONS.TheDrew.name:
                subscriptionId = CUSTOM_LOGIN_SUBSCRIPTIONS.TheDrew.id;
                break;
        }

        return subscriptionId;
    }

    onFormChange = (e) => {
        this.setState({
            ...this.state,
            [e.target.name]: e.target.value
        });
    }

    findUser = async (event) => {
        event?.preventDefault();
        event?.stopPropagation();

        if (!this.state.username || this.state.username.length < 1) {
            this.setState({
                isFormValid: false
            });
            return;
        }

        try {
            this.setState({ isLoading: true });
            const response = await this.props.accountActions.findUser(this.state.username.trim(), this.state.subscriptionId, this.state.redirectURL);
            if (response.type === types.GET_USERNAME_SUCCESS) {
                
                if (response.payload.isSSOUser === true) {
                    
                    if (response.payload.samlUrl && response.payload.samlUrl !== "") {
                        window.location.assign(response.payload.samlUrl);
                    }
                    else {
                        Notification("error", "Error", "An error occurred building the SSO redirect");
                    }

                } else {
                    this.setState({ showPasswordField: true, isLoading: false });
                }

            } else if (response.type === types.GET_USERNAME_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ isLoading: false });
            }
        } catch (error) {
            Notification("error", "An Error Occurred", error);
            this.setState({ isLoading: false });
        }
    }

    validateLogin = () => {
        return (this.state.username &&
            this.state.username.length > 1 &&
            this.state.password &&
            this.state.password.length > 1)
    }

    validate2faLogin = () => {
        return (this.state.twoFactorAuthCode && this.state.twoFactorAuthCode.length > 1)
    }

    loginUser = async (event) => {
        event.preventDefault();

        if (!this.validateLogin()) {
            this.setState({
                isFormValid: false
            });
            return;
        }

        this.setState({ isLoading: true });

        let { username, password } = this.state;

        try {
            const response = await this.props.accountActions.login({username, password}, this.state.subscriptionId);

            if (response.payload.requires2fa)
                this.setState({ show2fa: true });
            else
                this.redirect();
        } catch (error) {
            Notification("error", "Error", error, true);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    login2fa = async (event) => {
        event.preventDefault();

        if (!this.validate2faLogin()) {
            this.setState({
                is2faFormValid: false
            });
            return;
        }

        this.setState({ isLoading: true });

        let { twoFactorAuthCode, username, password } = this.state;

        try {
            const response = await this.props.mfaActions.login(twoFactorAuthCode.replace(/ /g, ''), username, password);
            if (response.type === types.MFA_LOGIN_SUCCESS) {
                this.redirect();
            } else if (response.type === types.MFA_LOGIN_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({
                    is2faFormValid: false
                });
            }
        } catch (error) {
            Notification("error", "Error", error, true);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    setToken = () => {
        this.props.accountActions.setSsoTempToken(this.state.token);
    }

    postSsologin = () => {
        this.props.accountActions.postSsologin()
            .then(() => {

                if (this.state.redirectToUserPage === true) {
                    this.props.accountActions.setRedirectURL('/profile/user');
                }

                this.redirect();
            })
            .catch(error => {
                Notification("error", "Error", error, true);
                this.setState({ isLoading: false });
            });
    }

    redirect() {
        this.props.history.push(Routes.Root);
    }

    render() {
        return (
            <LoginStyleWrapper>
                <LoadingIndicator isLoading={this.state.isLoading} isFullscreen={false} hideWhileLoading={true}>
                    { this.state.show2fa ?
                        <TfaSignInForm
                            onChange={this.onFormChange}
                            onSubmitCode={this.login2fa}
                            twoFactorAuthCode={this.state.twoFactorAuthCode}
                            isFormValid={this.state.is2faFormValid} />
                        :
                        <SignInForm
                            showPasswordField={this.state.showPasswordField}
                            username={this.state.username}
                            password={this.state.password}
                            onChange={this.onFormChange}
                            onSubmitUsername={this.findUser}
                            onSubmitCreds={this.loginUser}
                            isFormValid={this.state.isFormValid} />
                    }
                </LoadingIndicator>          
            </LoginStyleWrapper>
        );
    }
}

LoginPage.propTypes = {
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    redirectURL: PropTypes.string,
    error: PropTypes.string,
    token: PropTypes.string,
    redirectToUserPage: PropTypes.bool,
    accountActions: PropTypes.object.isRequired,
    userAccessRequestActions: PropTypes.object.isRequired,
    mfaActions: PropTypes.object.isRequired,
};

function mapDispatchToProps(dispatch) {
    return {
        accountActions: bindActionCreators({ ...accountActions }, dispatch),
        userAccessRequestActions: bindActionCreators({ ...userAccessRequestActions }, dispatch),
        mfaActions: bindActionCreators({ ...mfaActions }, dispatch)
    };
}

export default connect(null, mapDispatchToProps)(LoginPage);